import { c as _c } from "react/compiler-runtime";
import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { twMerge } from 'tailwind-merge';
import { InstanceForPaginationType } from '../types/instance-interface';
import { getRowId } from '../utils';
import { TableRowsProps } from './table-rows';
export type TableRowProps<T extends InstanceForPaginationType<T>> = React.PropsWithChildren<Pick<TableRowsProps<T>, 'data' | 'testAttributePostfix' | 'noHeader' | 'firstLineRect' | 'onRowClick' | 'nestedId' | 'paginationIdField' | 'rowIdParser' | 'withBorders' | 'isSortable' | 'className'> & {
  row: T;
  index?: number;
} & Omit<React.HTMLProps<HTMLDivElement>, 'data' | 'className'>>;
const animateLayoutChanges: AnimateLayoutChanges = args => defaultAnimateLayoutChanges({
  ...args,
  wasDragging: true
});
const TableRow = t0 => {
  const $ = _c(59);
  let children;
  let className;
  let data;
  let firstLineRect;
  let index;
  let isSortable;
  let nestedId;
  let noHeader;
  let onRowClick;
  let paginationIdField;
  let props;
  let row;
  let rowIdParser;
  let style;
  let testAttributePostfix;
  let withBorders;
  if ($[0] !== t0) {
    ({
      data,
      testAttributePostfix,
      noHeader,
      firstLineRect,
      onRowClick,
      nestedId,
      paginationIdField,
      rowIdParser,
      withBorders,
      row,
      index,
      className,
      children,
      style,
      isSortable,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = data;
    $[4] = firstLineRect;
    $[5] = index;
    $[6] = isSortable;
    $[7] = nestedId;
    $[8] = noHeader;
    $[9] = onRowClick;
    $[10] = paginationIdField;
    $[11] = props;
    $[12] = row;
    $[13] = rowIdParser;
    $[14] = style;
    $[15] = testAttributePostfix;
    $[16] = withBorders;
  } else {
    children = $[1];
    className = $[2];
    data = $[3];
    firstLineRect = $[4];
    index = $[5];
    isSortable = $[6];
    nestedId = $[7];
    noHeader = $[8];
    onRowClick = $[9];
    paginationIdField = $[10];
    props = $[11];
    row = $[12];
    rowIdParser = $[13];
    style = $[14];
    testAttributePostfix = $[15];
    withBorders = $[16];
  }
  let t1;
  if ($[17] !== nestedId || $[18] !== paginationIdField || $[19] !== row || $[20] !== rowIdParser) {
    t1 = getRowId({
      row,
      nestedId,
      paginationIdField,
      rowIdParser
    });
    $[17] = nestedId;
    $[18] = paginationIdField;
    $[19] = row;
    $[20] = rowIdParser;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  const id = t1;
  const t2 = !isSortable;
  let t3;
  if ($[22] !== id || $[23] !== t2) {
    t3 = {
      id,
      animateLayoutChanges,
      disabled: t2
    };
    $[22] = id;
    $[23] = t2;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  const {
    newIndex,
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform
  } = useSortable(t3);
  const rowIndex = newIndex !== -1 ? newIndex : index;
  let t4;
  if ($[25] !== transform) {
    t4 = CSS.Translate.toString(transform);
    $[25] = transform;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  const t5 = isDragging ? 0.5 : undefined;
  let t6;
  if ($[27] !== t4 || $[28] !== t5) {
    t6 = {
      transform: t4,
      opacity: t5
    };
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const dndStyle = t6;
  let t7;
  if ($[30] !== className || $[31] !== row) {
    t7 = typeof className === "string" ? className : className?.(row);
    $[30] = className;
    $[31] = row;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const receivedClassName = t7;
  let t8;
  if ($[33] !== dndStyle || $[34] !== style) {
    t8 = {
      ...dndStyle,
      ...style
    };
    $[33] = dndStyle;
    $[34] = style;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== onRowClick || $[37] !== row) {
    t9 = e => {
      const target = e.target as HTMLDivElement;
      const button = target.closest("button");
      const link = target.closest("a");
      const modalBackground = target.closest("#modal-background");
      if (button || link || modalBackground) {
        return;
      }
      if (onRowClick) {
        onRowClick(row);
      }
    };
    $[36] = onRowClick;
    $[37] = row;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const t10 = rowIndex === 0 && !firstLineRect && (noHeader ? "rounded-t-lg" : "rounded-t-lg lg:rounded-t-none");
  const t11 = rowIndex === (data?.items.length || 0) - 1 && "rounded-b-lg";
  const t12 = withBorders && "border-b-text-primary/15 border last:border-b-transparent";
  const t13 = isDragging ? " border-brand-primary border-dashed" : "border-transparent ";
  const t14 = isSortable ? "cursor-grab" : onRowClick ? "cursor-pointer" : "cursor-auto";
  let t15;
  if ($[39] !== receivedClassName || $[40] !== t10 || $[41] !== t11 || $[42] !== t12 || $[43] !== t13 || $[44] !== t14) {
    t15 = twMerge("hover:border-brand-primary main-transition-colors even:bg-brand-primary-subtle odd:bg-background-secondary flex flex-col items-start gap-4 border p-4 lg:flex-row lg:items-center lg:gap-0 lg:p-0", t10, t11, t12, t13, t14, receivedClassName);
    $[39] = receivedClassName;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== id || $[47] !== testAttributePostfix) {
    t16 = testAttributePostfix ? {
      "data-test-id": `${testAttributePostfix}-item-${id}`
    } : {};
    $[46] = id;
    $[47] = testAttributePostfix;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== attributes || $[50] !== children || $[51] !== listeners || $[52] !== props || $[53] !== setNodeRef || $[54] !== t15 || $[55] !== t16 || $[56] !== t8 || $[57] !== t9) {
    t17 = <div ref={setNodeRef} style={t8} {...attributes} {...listeners} onClick={t9} className={t15} {...t16} {...props}>{children}</div>;
    $[49] = attributes;
    $[50] = children;
    $[51] = listeners;
    $[52] = props;
    $[53] = setNodeRef;
    $[54] = t15;
    $[55] = t16;
    $[56] = t8;
    $[57] = t9;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  return t17;
};
export default TableRow;